import * as XLSX from "xlsx";
import { DetailFormComponentsEnum } from "./enum/detail-form-components.enum";
import { IFilterableClass, PaginationFilterListElement } from "./filterable";
import { flattenObject } from "./flatten-object";
import { IFormableClass, IMDetailFormConfig } from "./formable";
import { IExcelImportConfig } from "./interfaces/excel-import.interface";
import { $t } from "./utility/t";
/**
 *
 * @param entity
 * @returns
 */
export function getTranslationsFromEntity(entity: IFormableClass | IFilterableClass) {
  const formables = (entity as IFormableClass).formables;
  if (formables?.length) {
    return getTranslationsFromFormable(formables);
  }

  const filterables = (entity as IFilterableClass).filterables;
  if (filterables?.length) {
    return getTranslationsFromFilterable(filterables);
  }

  return new Map();
}

export function getTranslationsFromFormable(formables: IMDetailFormConfig<any>[]) {
  return new Map(
    formables.map(formable => [formable.key, formable.props?.label ? $t(formable.props.label) : formable.key])
  );
}

export function getTranslationsFromFilterable(filterables: PaginationFilterListElement[]) {
  return new Map(
    filterables.map(formable => [formable.key, formable.displayName ? $t(formable.displayName) : formable.key])
  );
}

/**
 * something like vehicle.contract.0.some.stuff.2.yup will become $t(vehicle.contract.some.stuff) [0] [2]
 */
export function transformKeyToColumnName(key: string, translations: Map<string, string>, addKey?: boolean) {
  // find all numbers in the key e.g. vehicle.contract.0.some.stuff.2.yup will return [0, 2]
  const matches = key.matchAll(RegExp(/\d/, "g"));
  // create a string with the indexes e.g. [0] [2]
  const indexes =
    Array.from(matches)
      .map(d => `[${d}]`)
      .join("") || undefined;
  // remove the index numbers
  const clearedString = key.replaceAll(RegExp(/.\d/, "g"), "");

  if (!translations.get(clearedString)) {
    return key;
  }

  const translatedClearedString = translations.get(clearedString);
  const translatedClearedStringWithIndex = [translatedClearedString, indexes].join(" ");

  if (addKey) {
    return `${translatedClearedStringWithIndex} (${key})`;
  }

  return `${translatedClearedStringWithIndex}`;
}

/**
 * Create a list of flattened objects where each object represents a row in a csv export
 * If an object is missing a key that another object in the input possesses, the value will be an empty string
 *
 * @param docs the document to be exported
 * @param headerTranslations map of key value pairs for the properties key to a translated result
 * @param sort should the keys(columns) be ordered alphabetically. Defaults to true
 * @param addKey configures the download to contain the exported properties keys in brackets in the header
 * @returns
 */
export function createXlsxObject(
  docs: Record<string, any>[],
  headerTranslations?: Map<string, string>,
  sort = true,
  addKey = true
) {
  const exportData: Record<string, string>[] = [];

  const listOfFlattenedElements = docs.map(element => flattenObject(element));
  let uniqueKeys: string[] = Array.from(new Set(listOfFlattenedElements.map(e => Object.keys(e)).flat()));

  if (sort) {
    uniqueKeys = uniqueKeys.sort((a: string, b: string) => (a < b ? -1 : 1));
  }

  let uniqueKeysTranslated = new Map(uniqueKeys.map(key => [key, key]));
  if (headerTranslations) {
    uniqueKeysTranslated = new Map(
      uniqueKeys.map(key => {
        const translated = transformKeyToColumnName(key, headerTranslations, addKey);

        return [key, translated];
      })
    );
  }

  for (const element of listOfFlattenedElements) {
    const exportDataPoint: Record<string, string> = {};
    for (const [key, translation] of uniqueKeysTranslated.entries()) {
      exportDataPoint[translation] = element[key] ?? "";
    }
    exportData.push(exportDataPoint);
  }

  return exportData;
}

export function createXlsxObjectSimple(docs: Record<string, any>[], headerTranslations?: Map<string, string>) {
  const exportData: Record<string, any>[] = [];

  if (!headerTranslations) {
    throw new Error("headerTranslations is required for simple export");
  }

  for (const doc of docs) {
    const row: Record<string, any> = {};
    for (const [key, translated] of headerTranslations.entries()) {
      row[translated] = doc[key] ?? "";
    }

    exportData.push(row);
  }

  return exportData;
}

export function simpleExcelDownload(docs: Record<string, any>[], headerTranslations?: Map<string, string>) {
  const exportData = createXlsxObjectSimple(docs, headerTranslations);

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "exp");
  XLSX.writeFile(workbook, "export_" + new Date().toISOString() + ".xlsx", { compression: true });
}

/**
 * Download the given data as a xslx file
 * @see createXlsxObject
 *
 * @param docs
 * @param headerTranslations
 * @param sort should the keys(columns) be ordered alphabetically. Defaults to true
 * @param addKey configures the download to contain the exported properties keys in brackets in the header
 * @param title
 */
export function downloadAsXlsx(
  docs: Record<string, any>[],
  headerTranslations?: Map<string, string>,
  sort?: boolean,
  addKey?: boolean,
  title: string = "exp_" + new Date().toISOString()
) {
  const exportData = createXlsxObject(docs, headerTranslations, sort, addKey);

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "exp");
  XLSX.writeFile(workbook, title + ".xlsx", { compression: true });
}

export function getExcelImportConfig(formConfig: IMDetailFormConfig[]) {
  let importConfig: { [key: string]: IExcelImportConfig } = {};
  const headerTranslations: Map<string, string> = new Map(
    formConfig.map(formConfigItem => [formConfigItem.key, $t(formConfigItem.props?.label) ?? formConfigItem.key])
  );

  for (const formConfigItem of formConfig) {
    const identifer = transformKeyToColumnName(formConfigItem.key, headerTranslations);

    const config: IExcelImportConfig = {
      label: identifer,
      hint: formConfigItem.props.label ? $t(formConfigItem.props.hint) : "",
      required: formConfigItem.props.required ?? false,
      import: formConfigItem.props.required ?? false,
      originColumnNameInExcelFile: identifer,
      transform: (value: string | number | boolean) => {
        if (formConfigItem.props.rules) {
          const fails: string[] = [];
          for (const rule of formConfigItem.props.rules) {
            const fail = rule(value);
            if (fail !== true) fails.push(fail);
          }
          if (fails.length) {
            throw new Error(`${identifer}: ${fails.join(", ")}`);
          }
        }

        const items: string[] = [];
        for (const item of formConfigItem.props.items ?? []) {
          if (item?.value) {
            items.push(item.value);
          } else if (item) {
            items.push(item);
          }
        }
        if (
          [DetailFormComponentsEnum.SELECT_FIELD, DetailFormComponentsEnum.AUTO_COMPLETE].includes(
            formConfigItem.type
          ) &&
          items.length
        ) {
          if (items.length) {
            const found = items.find((item: any) => item == value); // not ===, but == to accomodate string/number comparison
            if (found === undefined) {
              throw new Error(
                $t("sign.Sign.error.invalidType", {
                  type: identifer,
                  expectedType: Object.values(items).join(` ${$t("or")} `),
                  receivedType: value
                })
              );
            }
          }
        }

        if (formConfigItem.props?.type) {
          if (formConfigItem.props.type === "date") {
            if (!value) {
              return "";
            }

            // Excel counts days since 1900-01-01 for type date
            if (typeof value === "number") {
              const serial = value as number;
              const excelEpoch = new Date("1900-01-01T00:00:00.000Z"); // January 1, 1900
              const daysOffset = serial - 2; // Date is off by 2 days
              const date = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);

              return date.toISOString();
            }

            return new Date(value.toString()).toISOString();
          }

          if (formConfigItem.props.type === "number") {
            return Number(value.toString());
          }

          if (formConfigItem.props.type === "boolean") {
            return Boolean(value);
          }

          return value.toString();
        }

        return value.toString();
      }
    };

    importConfig = {
      ...importConfig,
      [formConfigItem.key]: config
    };
  }

  return importConfig;
}
